import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DonkeyGuide, DonkeyMascot } from "@/components/DonkeyMascot";
import { PublicAmbient, KineticHeading, Reveal } from "@/components/public/PublicVisuals";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send, Check } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const FIELD = "bg-donkey-bg border-white/15 mt-1 transition-colors focus-visible:border-donkey-orange/60 focus-visible:ring-1 focus-visible:ring-donkey-orange/30";
const RAW_FIELD = "w-full bg-donkey-bg border border-white/15 rounded-lg px-3 py-2 text-sm mt-1 transition-colors focus:border-donkey-orange/60 focus:outline-none focus:ring-1 focus:ring-donkey-orange/30";

const CATEGORIES = [
  ["general", "General question"], ["account", "Account support"], ["technical", "Technical problem"],
  ["report", "Report or analysis question"], ["billing", "Billing question"],
  ["business_enquiry", "Business or Enterprise enquiry"], ["complaint", "Complaint"],
  ["accessibility", "Accessibility"], ["privacy", "Privacy or data request"], ["other", "Other"],
];

export default function ContactPage() {
  const reduce = useReducedMotion();
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "general", message: "", account_reference: "", consent: false, company: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/support/contact", form); setDone(data.reference); toast.success("Enquiry received"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send"); }
    finally { setBusy(false); }
  };
  if (done) return (
    <div className="relative overflow-hidden min-h-screen flex items-center justify-center px-5">
      <PublicAmbient intensity={0.4} />
      <motion.div
        className="relative z-10 card-donkey rounded-2xl p-8 max-w-md text-center"
        data-testid="contact-success"
        initial={reduce ? false : { opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, ease: EASE }}
      >
        <motion.div
          initial={reduce ? false : { scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: EASE }}
        >
          <CheckCircle2 className="w-12 h-12 text-donkey-green mx-auto mb-4" />
        </motion.div>
        <h1 className="font-heading font-bold text-2xl text-donkey-warm">Thank you</h1>
        <p className="text-donkey-grey mt-2">Your reference is <span className="text-donkey-orange font-mono">{done}</span>. We reply inside your account — The Honest Donkey doesn't use a support email address.</p>
      </motion.div>
    </div>
  );
  return (
    <div className="relative overflow-hidden min-h-screen">
      <PublicAmbient intensity={0.4} />
      <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8 py-14">
        <div className="flex items-center gap-5">
          <DonkeyMascot size={104} alt="" className="hidden sm:block shrink-0 opacity-95 donkey-alive drop-shadow-[0_0_40px_rgba(249,115,22,0.2)]" />
          <KineticHeading as="h1" text="Contact us" className="text-4xl sm:text-5xl" />
        </div>
        <Reveal y={20} delay={0.1}>
          <DonkeyGuide title="We don't use a support email" className="mt-6">Send us a message here and we'll reply inside your account. For faster help, sign in and use the Support Centre or ask DonkeyBot.</DonkeyGuide>
        </Reveal>
        <div className="mt-6 grid lg:grid-cols-[1fr_250px] gap-6 items-start">
          <Reveal y={24} delay={0.18}>
          <form onSubmit={submit} className="space-y-4 card-donkey rounded-2xl p-6 sm:p-7" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label htmlFor="c-name" className="text-sm text-donkey-grey">Name</label><Input id="c-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={FIELD} data-testid="contact-name" /></div>
              <div><label htmlFor="c-email" className="text-sm text-donkey-grey">Email</label><Input id="c-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={FIELD} data-testid="contact-email" /></div>
            </div>
            <div><label htmlFor="c-subject" className="text-sm text-donkey-grey">Subject</label><Input id="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required className={FIELD} data-testid="contact-subject" /></div>
            <div><label htmlFor="c-category" className="text-sm text-donkey-grey">Category</label>
              <select id="c-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={RAW_FIELD} data-testid="contact-category">
                {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><label htmlFor="c-ref" className="text-sm text-donkey-grey">Account reference (optional)</label><Input id="c-ref" value={form.account_reference} onChange={(e) => setForm({ ...form, account_reference: e.target.value })} className={FIELD} data-testid="contact-ref" /></div>
            <div><label htmlFor="c-message" className="text-sm text-donkey-grey">Message</label><textarea id="c-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required rows={5} className={RAW_FIELD} data-testid="contact-message" /></div>
            {/* Honeypot — visually hidden, must remain empty */}
            <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="company" />
            <label className="flex items-start gap-2 text-sm text-donkey-grey"><input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-donkey-orange" data-testid="contact-consent" /> I agree that my details will be stored and used to respond to my enquiry, per the privacy notice.</label>
            <Button type="submit" disabled={busy} className="bg-donkey-orange text-donkey-black font-semibold h-11 px-6 transition-transform hover:-translate-y-0.5" data-testid="contact-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Send enquiry</>}</Button>
          </form>
          </Reveal>
          {/* live conversation profile — completes as the message takes shape */}
          <aside className="lg:sticky lg:top-6 card-donkey rounded-2xl p-5" data-testid="contact-profile">
            <p className="text-[11px] uppercase tracking-[0.15em] text-donkey-orange mb-4">Your message</p>
            <ul className="space-y-2.5">
              {[["Name", form.name], ["Email", form.email], ["Subject", form.subject], ["Message", form.message], ["Consent", form.consent]].map(([l, filled]) => (
                <li key={l} className="flex items-center gap-2.5 text-sm">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center border transition-colors ${filled ? "border-donkey-green/50 bg-donkey-green/15 text-donkey-green" : "border-white/15 text-donkey-grey/40"}`}>
                    {filled ? <Check className="w-3 h-3" /> : <span className="w-1.5 h-1.5 rounded-full bg-white/20" />}
                  </span>
                  <span className={filled ? "text-donkey-warm" : "text-donkey-grey"}>{l}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-white/10">
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                <motion.div className="h-full bg-gradient-to-r from-donkey-orange to-donkey-warmorange" initial={false}
                  animate={{ width: `${([form.name, form.email, form.subject, form.message, form.consent].filter(Boolean).length / 5) * 100}%` }}
                  transition={{ type: "spring", stiffness: 140, damping: 20 }} />
              </div>
              <p className="text-xs text-donkey-grey mt-2">{[form.name, form.email, form.subject, form.message, form.consent].every(Boolean) ? "Ready to send." : "The Donkey's listening…"}</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
